import { Link, useMatches } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { Fragment, useEffect, type ComponentType, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

export type CrumbComponent = ComponentType<{ params: Record<string, string> }>

/** A breadcrumb a route supplies through `staticData.crumbTrail`. */
export interface CrumbTrailItem {
  pathname: string
  /** A label, or a crumb component rendered with these params. */
  crumb: string | CrumbComponent
  params?: Record<string, string>
}

export type CrumbTrail = (search: Record<string, unknown>) => CrumbTrailItem[] | undefined

interface Crumb {
  id: string
  pathname: string
  node: ReactNode
  text?: string
}

/** Breadcrumbs from each matched route's `staticData.crumb`. */
export function Breadcrumbs({ className }: { className?: string }) {
  const matches = useMatches()
  const crumbs: Crumb[] = []
  const push = (
    id: string,
    pathname: string,
    crumb: string | CrumbComponent,
    params: Record<string, string>,
  ) => {
    if (typeof crumb === 'string') crumbs.push({ id, pathname, node: crumb, text: crumb })
    else {
      const C = crumb
      crumbs.push({ id, pathname, node: <C params={params} /> })
    }
  }
  for (const m of matches) {
    const params = m.params as Record<string, string>
    const trail = m.staticData?.crumbTrail?.((m.search ?? {}) as Record<string, unknown>)
    if (trail) {
      crumbs.length = 0
      trail.forEach((t, i) => push(`${m.id}#trail-${i}`, t.pathname, t.crumb, t.params ?? {}))
    }
    const crumb = m.staticData?.crumb
    if (crumb) push(m.id, m.pathname, crumb, params)
  }
  // Keep the document title in step with the deepest text crumb.
  const title = [...crumbs].reverse().find((c) => c.text)?.text
  useEffect(() => {
    document.title = title && title !== 'Dashboard' ? `${title} – MailSmith` : 'MailSmith'
  }, [title])

  if (crumbs.length === 0) return null
  return (
    <nav aria-label="Breadcrumb" className={cn('min-w-0', className)}>
      <ol className="flex min-w-0 items-center gap-1 text-sm">
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1
          return (
            <Fragment key={c.id}>
              {i > 0 && <ChevronRight className="size-3.5 shrink-0 text-ink-3" aria-hidden />}
              <li className={cn('min-w-0 truncate', last ? 'font-medium text-ink' : 'text-ink-2')}>
                {last ? (
                  <span aria-current="page">{c.node}</span>
                ) : (
                  <Link
                    to={c.pathname}
                    className="rounded-sm hover:text-ink focus-visible:outline-2 focus-visible:outline-focus"
                  >
                    {c.node}
                  </Link>
                )}
              </li>
            </Fragment>
          )
        })}
      </ol>
    </nav>
  )
}
